/**
 * Centralized API client for communicating with Cloudflare Worker.
 */

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || '';
const API_BASE = `${API_ORIGIN}/api/v1`;

/**
 * Custom API Error class with normalized error code and status.
 */
export class ApiError extends Error {
  constructor(code, message, status, meta = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.meta = meta;
  }
}

/**
 * Universal fetch wrapper for API v1.
 *
 * @param {string} endpoint
 * @param {RequestInit} [options]
 * @returns {Promise<any>}
 */
export async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...options.headers,
  };
  const csrfToken = sessionStorage.getItem('sbc_admin_csrf');
  if (csrfToken && !['GET', 'HEAD'].includes(options.method || 'GET')) headers['X-CSRF-Token'] = csrfToken;

  try {
    const response = await fetch(url, { credentials: 'include', ...options, headers });
    const json = await response.json().catch(() => null);

    if (!response.ok || !json?.success) {
      const code = json?.error?.code || 'NETWORK_ERROR';
      const message = json?.error?.message || `Request failed with status ${response.status}`;
      throw new ApiError(code, message, response.status, json?.meta || {});
    }

    return json;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('NETWORK_ERROR', error.message || 'Failed to connect to server', 0);
  }
}

export const api = {
  getHealth: () => apiRequest('/health'),
  getStoreConfig: () => apiRequest('/store/config'),
  getCategories: () => apiRequest('/categories'),
  getProducts: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        query.append(key, String(val));
      }
    });
    const qs = query.toString();
    return apiRequest(`/products${qs ? `?${qs}` : ''}`);
  },
  getProductBySlug: (slug) => apiRequest(`/products/${encodeURIComponent(slug)}`),
  
  // Phase 3 COD Order Endpoints
  getFormToken: () => apiRequest('/orders/form-token'),
  createQuickOrder: (payload, idempotencyKey = null) => {
    const headers = {};
    if (idempotencyKey) {
      headers['Idempotency-Key'] = idempotencyKey;
    }
    return apiRequest('/orders/quick', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  },
  getOrderStatus: (orderNumber, trackingToken) => {
    return apiRequest(`/orders/${encodeURIComponent(orderNumber)}/status`, {
      method: 'GET',
      headers: {
        'X-Order-Tracking-Token': trackingToken,
      },
    });
  },
  adminLogin: async (email, password) => {
    const response = await apiRequest('/admin/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
    sessionStorage.setItem('sbc_admin_csrf', response.data.csrfToken);
    return response;
  },
  adminAuthStatus: () => apiRequest('/admin/auth/status'),
  adminBootstrap: (payload, bootstrapToken) => apiRequest('/admin/auth/bootstrap', { method: 'POST', headers: { 'X-Admin-Bootstrap-Token': bootstrapToken }, body: JSON.stringify(payload) }),
  adminMe: () => apiRequest('/admin/auth/me'),
  adminLogout: async () => { const response = await apiRequest('/admin/auth/logout', { method: 'POST' }); sessionStorage.removeItem('sbc_admin_csrf'); return response; },
  adminDashboard: () => apiRequest('/admin/dashboard'),
  adminOrders: (params = {}) => apiRequest(`/admin/orders?${new URLSearchParams(params)}`),
  adminOrder: (id) => apiRequest(`/admin/orders/${id}`),
  adminOrderStatus: (id, status, comment = '') => apiRequest(`/admin/orders/${id}/status`, { method: 'POST', body: JSON.stringify({ status, comment }) }),
  adminProducts: (params = {}) => apiRequest(`/admin/products?${new URLSearchParams(params)}`),
  adminUpdateProduct: (id, payload) => apiRequest(`/admin/products/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  adminAudit: () => apiRequest('/admin/audit'),
  adminAccounts: () => apiRequest('/admin/accounts'),
  adminCreateAccount: (payload) => apiRequest('/admin/accounts', { method: 'POST', body: JSON.stringify(payload) }),
  adminUpdateAccount: (id, payload) => apiRequest(`/admin/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
};
